import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, ClipboardList } from "lucide-react";
import { exportCsv } from "@/lib/exportCsv";
import { useParts } from "@/hooks/useParts";
import { useAssets } from "@/hooks/useAssets";
import { getPartLocationStock, loadAllPartLocationStock } from "@/hooks/useInventoryLocations";
import { getPartAssetLabels, loadAllPartAssets } from "@/hooks/usePartAssets";
import type { Part } from "@/hooks/useParts";
import type { AssetStatus } from "@/hooks/useAssets";
import { cn } from "@/lib/utils";

// ── Parts export ──────────────────────────────────────────────────────────

const PARTS_COLUMNS = [
  { header: "Name",             key: "name" },
  { header: "OEM Part #",       key: "part_number" },
  { header: "Linked Assets",    key: "_linked_assets" },
  { header: "Location",         key: "_location" },
  { header: "Qty at Location",  key: "_location_qty" },
  { header: "Total In Stock",   key: "qty_in_stock" },
  { header: "Available",        key: "qty_available" },
  { header: "Reserved",         key: "qty_reserved" },
  { header: "On Order",         key: "qty_ordered" },
  { header: "Min Qty",          key: "min_quantity" },
  { header: "Unit",             key: "unit" },
  { header: "Unit Cost",        key: "unit_cost_fmt" },
  { header: "Status",           key: "_status" },
  { header: "Notes",            key: "notes" },
];

function buildPartsRows(parts: Part[], getLocationStock: (partId: string) => { location_name: string; parent_location_name: string | null; quantity: number }[]) {
  const rows: Record<string, string | number | null | undefined>[] = [];

  for (const p of parts) {
    const base = {
      name: p.name,
      part_number: p.part_number,
      _linked_assets: getPartAssetLabels(p.id).join(", ") || "",
      qty_in_stock: p.qty_in_stock,
      qty_available: p.qty_in_stock - p.qty_reserved,
      qty_reserved: p.qty_reserved,
      qty_ordered: p.qty_ordered,
      min_quantity: p.min_quantity,
      unit: p.unit,
      unit_cost_fmt: p.unit_cost != null ? `$${p.unit_cost.toFixed(2)}` : "",
      _status: p.is_low_stock ? "Low Stock" : "OK",
      notes: p.notes,
    };

    const stock = getLocationStock(p.id);

    if (stock.length === 0) {
      // No location data yet — single row showing total
      rows.push({ ...base, _location: "Unassigned", _location_qty: p.qty_in_stock });
    } else {
      // One row per location
      for (const s of stock) {
        const locationLabel = s.parent_location_name
          ? `${s.parent_location_name} › ${s.location_name}`
          : s.location_name;
        rows.push({ ...base, _location: locationLabel, _location_qty: s.quantity });
      }
      // Total row
      rows.push({ ...base, _location: "TOTAL", _location_qty: stock.reduce((sum, s) => sum + s.quantity, 0) });
    }
  }

  return rows;
}

// ── Count sheet ───────────────────────────────────────────────────────────

const COUNT_SHEET_COLUMNS = [
  { header: "Name",          key: "name" },
  { header: "OEM Part #",    key: "part_number" },
  { header: "Linked Assets", key: "_linked_assets" },
  { header: "Location",      key: "_location" },
  { header: "Expected Qty",  key: "_expected_qty" },
  { header: "Counted Qty",   key: "_counted" },
  { header: "Variance",      key: "_variance" },
  { header: "Unit",          key: "unit" },
  { header: "Min Qty",       key: "min_quantity" },
  { header: "Notes",         key: "_count_notes" },
];

function buildCountSheetRows(parts: Part[], getLocationStock: (partId: string) => { location_name: string; parent_location_name: string | null; quantity: number }[]) {
  const rows: Record<string, string | number | null | undefined>[] = [];

  for (const p of parts) {
    const base = {
      name: p.name,
      part_number: p.part_number,
      _linked_assets: getPartAssetLabels(p.id).join(", ") || "",
      unit: p.unit,
      min_quantity: p.min_quantity,
      _counted: "",
      _variance: "",
      _count_notes: "",
    };

    const stock = getLocationStock(p.id);

    if (stock.length === 0) {
      rows.push({ ...base, _location: "Unassigned", _expected_qty: p.qty_in_stock });
    } else {
      for (const s of stock) {
        const locationLabel = s.parent_location_name
          ? `${s.parent_location_name} › ${s.location_name}`
          : s.location_name;
        rows.push({ ...base, _location: locationLabel, _expected_qty: s.quantity });
      }
    }
  }

  return rows;
}

// ── Dialog ────────────────────────────────────────────────────────────────

export function PartsExportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const { data: allParts = [], isLoading } = useParts();

  const filtered = lowStockOnly ? allParts.filter((p) => p.is_low_stock) : allParts;
  const date = new Date().toISOString().slice(0, 10);

  const handleExportInventory = async () => {
    const ids = filtered.map((p) => p.id);
    await Promise.all([loadAllPartLocationStock(ids), loadAllPartAssets(ids)]);
    const rows = buildPartsRows(filtered, getPartLocationStock);
    exportCsv(rows, PARTS_COLUMNS, `parts-inventory-${date}.csv`);
    onOpenChange(false);
  };

  const handleExportCountSheet = async () => {
    const ids = filtered.map((p) => p.id);
    await Promise.all([loadAllPartLocationStock(ids), loadAllPartAssets(ids)]);
    const rows = buildCountSheetRows(filtered, getPartLocationStock);
    exportCsv(rows, COUNT_SHEET_COLUMNS, `parts-count-sheet-${date}.csv`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Export Parts</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Low stock only</Label>
            <button
              onClick={() => setLowStockOnly((v) => !v)}
              className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                lowStockOnly ? "bg-primary" : "bg-muted-foreground/30"
              )}
            >
              <span className={cn(
                "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform",
                lowStockOnly ? "translate-x-4" : "translate-x-0.5"
              )} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {isLoading ? "Loading…" : `${filtered.length} part${filtered.length !== 1 ? "s" : ""} selected`}
          </p>

          <div className="border-t pt-4 space-y-2">
            <button
              onClick={handleExportInventory}
              disabled={isLoading || filtered.length === 0}
              className="w-full flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
            >
              <Download className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Inventory Export</p>
                <p className="text-xs text-muted-foreground">Full details — qty by location, cost, status</p>
              </div>
            </button>

            <button
              onClick={handleExportCountSheet}
              disabled={isLoading || filtered.length === 0}
              className="w-full flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left disabled:opacity-50"
            >
              <ClipboardList className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Inventory Count Sheet</p>
                <p className="text-xs text-muted-foreground">Blank form to fill in counted quantities</p>
              </div>
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Assets export ─────────────────────────────────────────────────────────

const ASSETS_COLUMNS = [
  { header: "Name",           key: "name" },
  { header: "Category",       key: "_type" },
  { header: "Status",         key: "_status" },
  { header: "Criticality",    key: "criticality" },
  { header: "Make",           key: "make" },
  { header: "Model",          key: "model" },
  { header: "Serial #",       key: "serial_number" },
  { header: "Year Installed", key: "year_installed" },
  { header: "Description",    key: "description" },
  { header: "Notes",          key: "notes" },
];

const STATUS_OPTIONS: { value: AssetStatus | "all"; label: string }[] = [
  { value: "all",            label: "All statuses" },
  { value: "online",         label: "Online" },
  { value: "offline",        label: "Offline" },
  { value: "maintenance",    label: "In Maintenance" },
  { value: "decommissioned", label: "Decommissioned" },
];

const TYPE_LABELS: Record<string, string> = {
  bottling: "Bottling", infrastructure: "Infrastructure", safety: "Safety",
  wine_production: "Wine Production", vehicle: "Vehicle", tools: "Tools",
  fluids: "Fluids", tank: "Tank",
};

export function AssetsExportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [statusFilter, setStatusFilter] = useState<AssetStatus | "all">("all");
  const { data: allAssets = [], isLoading } = useAssets();

  const filtered = statusFilter === "all" ? allAssets : allAssets.filter((a) => a.status === statusFilter);

  const handleExport = () => {
    const rows = filtered.map((a) => ({
      ...a,
      _type: a.asset_type ? (TYPE_LABELS[a.asset_type] ?? a.asset_type) : "",
      _status: a.status.replace("_", " "),
    }));
    exportCsv(rows, ASSETS_COLUMNS, `assets-inventory-${new Date().toISOString().slice(0, 10)}.csv`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Export Assets Inventory</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-sm">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AssetStatus | "all")}
              className="w-full text-sm bg-background border rounded-md px-3 py-2"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {isLoading ? "Loading…" : `${filtered.length} asset${filtered.length !== 1 ? "s" : ""} will be exported`}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleExport} disabled={isLoading || filtered.length === 0} className="gap-1.5">
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
